import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ArrowLeft } from "lucide-react";
import AddressList from "./AddressList";
import AddressForm from "./AddressForm";
import { getAddressId } from "../../utils/address";

/**
 * AddressModal
 *
 * A combined address management modal:
 *  - "list" view  → shows saved addresses, edit/delete/add-new
 *  - "form" view  → add new or edit existing address
 *
 * Props:
 *  open           : boolean
 *  onClose()      : close without changing
 *  onConfirm(id)  : called when user clicks "Confirm" on the list view
 *  addresses      : address[]
 *  selectedId     : number | null
 *  loading        : boolean
 *  onSelect(id)   : select an address from the list
 *  onCreate(data) : async – submit new address
 *  onUpdate(id, data) : async – submit edit
 *  onDelete(id)   : async – delete address
 *  onSetDefault(id)   : async – set address as default
 *  confirmOnSave  : boolean – immediately apply saved address to checkout
 *  isDark         : boolean
 */
export default function AddressModal({
  open,
  onClose,
  onConfirm,
  addresses,
  selectedId,
  loading,
  onSelect,
  onCreate,
  onUpdate,
  onDelete,
  onSetDefault,
  initialView = "list",
  initialEditingAddress = null,
  confirmOnSave = false,
  isDark,
}) {
  // "list" | "form"
  const [view, setView] = useState("list");
  const [editingAddr, setEditingAddr] = useState(null);
  const [formError, setFormError] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [listError, setListError] = useState(null);
  const [draftSelectedId, setDraftSelectedId] = useState(null);

  // Reset to list whenever modal opens
  useEffect(() => {
    if (!open) return;

    const startInForm = initialView === "form" || Boolean(initialEditingAddress);
    setView(startInForm ? "form" : "list");
    setEditingAddr(initialEditingAddress ?? null);
    setFormError(null);
    setDeleteConfirmId(null);
    setListError(null);
  }, [initialEditingAddress, initialView, open]);

  useEffect(() => {
    if (!open) return;
    setDraftSelectedId(selectedId ?? null);
  }, [open, selectedId]);

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const handleEdit = useCallback((addr) => {
    setEditingAddr(addr);
    setFormError(null);
    setListError(null);
    setView("form");
  }, []);

  const handleAddNew = useCallback(() => {
    setEditingAddr(null);
    setFormError(null);
    setListError(null);
    setView("form");
  }, []);

  const handleSelect = useCallback(
    (id) => {
      setDraftSelectedId(id ?? null);
    },
    []
  );

  const handleFormSubmit = useCallback(
    async (data) => {
      setFormError(null);
      setListError(null);
      try {
        if (editingAddr) {
          const editingId = getAddressId(editingAddr);
          const updated = await onUpdate(editingId, data);
          const savedId = getAddressId(updated) ?? editingId;

          if (confirmOnSave && savedId != null) {
            setDraftSelectedId(savedId);
            setView("list");
            setEditingAddr(null);
            if (typeof onConfirm === "function") {
              onConfirm(savedId);
            } else {
              onSelect?.(savedId);
            }
            return;
          }

          setView("list");
          setEditingAddr(null);
        } else {
          const created = await onCreate(data);
          const createdId = getAddressId(created);

          // Keep modal in list view so the user can see the newly saved address.
          if (createdId != null) {
            setDraftSelectedId(createdId);
            if (confirmOnSave) {
              setView("list");
              setEditingAddr(null);
              if (typeof onConfirm === "function") {
                onConfirm(createdId);
              } else {
                onSelect?.(createdId);
              }
              return;
            }
          }

          setView("list");
          setEditingAddr(null);
        }
      } catch (err) {
        setFormError(err?.message || "Failed to save address. Please try again.");
      }
    },
    [confirmOnSave, editingAddr, onConfirm, onCreate, onSelect, onUpdate]
  );

  const handleDeleteRequest = useCallback((id) => {
    setListError(null);
    setDeleteConfirmId(id);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteConfirmId) return;
    try {
      setListError(null);
      await onDelete(deleteConfirmId);
      setDraftSelectedId((current) =>
        String(current) === String(deleteConfirmId) ? null : current
      );
    } catch (err) {
      setListError(err?.message || "Failed to delete address. Please try again.");
    } finally {
      setDeleteConfirmId(null);
    }
  }, [deleteConfirmId, onDelete]);

  const handleSetDefault = useCallback(
    async (id) => {
      try {
        setListError(null);
        await onSetDefault(id);
        setDraftSelectedId(id);
      } catch (err) {
        setListError(err?.message || "Failed to update your default address. Please try again.");
      }
    },
    [onSetDefault]
  );

  const handleConfirm = () => {
    if (draftSelectedId == null) return;

    if (typeof onConfirm === "function") {
      onConfirm(draftSelectedId);
      return;
    }

    onSelect?.(draftSelectedId);
  };

  if (!open) return null;

  const overlayBase = "fixed inset-0 z-50 flex";
  const backdropCls = "fixed inset-0 bg-black/50 backdrop-blur-sm";
  // Mobile: slide-up drawer. Desktop: centered modal.
  const panelCls = [
    // Mobile
    "fixed bottom-0 left-0 right-0 w-full rounded-t-3xl max-h-[90dvh] flex flex-col pb-[env(safe-area-inset-bottom)]",
    // Desktop override
    "sm:relative sm:bottom-auto sm:left-auto sm:right-auto sm:rounded-2xl sm:max-h-[80vh] sm:w-full sm:max-w-lg sm:m-auto sm:pb-0",
    "border border-neutral-200 bg-white shadow-2xl dark:border-neutral-800 dark:bg-neutral-900",
    "overflow-hidden",
  ].join(" ");
  const headerBorder = "border-neutral-100 dark:border-neutral-800";

  const heading =
    view === "form"
      ? editingAddr
        ? "Edit Address"
        : "Add New Address"
      : "My Addresses";

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div className={overlayBase} aria-modal="true" role="dialog">
      {/* Backdrop */}
      <div className={backdropCls} onClick={onClose} />

      {/* Panel */}
      <div className={panelCls}>
        {/* Header */}
        <div className={`flex items-center gap-2 px-5 py-4 border-b ${headerBorder} shrink-0`}>
          {view === "form" && (
            <button
              type="button"
              onClick={() => { setView("list"); setEditingAddr(null); setFormError(null); }}
              className="p-1 rounded-lg transition text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
            >
              <ArrowLeft size={18} />
            </button>
          )}
          <h2 className="flex-1 text-sm font-bold uppercase tracking-widest text-neutral-900 dark:text-neutral-100">
            {heading}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg transition text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {view === "list" ? (
            <>
              {listError && (
                <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-600 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-400">
                  {listError}
                </div>
              )}
              <AddressList
                addresses={addresses}
                selectedId={draftSelectedId}
                onSelect={handleSelect}
                onEdit={handleEdit}
                onDelete={handleDeleteRequest}
                onSetDefault={handleSetDefault}
                onAddNew={handleAddNew}
                isDark={isDark}
                loading={loading}
              />
            </>
          ) : (
            <AddressForm
              initialData={editingAddr}
              onSubmit={handleFormSubmit}
              onCancel={() => { setView("list"); setEditingAddr(null); setFormError(null); }}
              isDark={isDark}
              submitError={formError}
            />
          )}
        </div>

        {/* Footer – only on list view */}
        {view === "list" && (
          <div className={`px-5 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:pb-4 border-t ${headerBorder} shrink-0`}>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={draftSelectedId == null}
              className="w-full rounded-full bg-neutral-950 py-3.5 text-sm font-bold text-white transition hover:bg-neutral-800 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 dark:bg-white dark:text-neutral-950 dark:hover:bg-neutral-200"
            >
              Confirm Address
            </button>
          </div>
        )}
      </div>

      {/* Delete confirmation dialog */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteConfirmId(null)} />
          <div className="relative w-full max-w-sm rounded-2xl border border-neutral-200 bg-white p-6 shadow-2xl dark:border-neutral-800 dark:bg-neutral-900">
            <h3 className="text-sm font-bold mb-2 text-neutral-900 dark:text-neutral-100">
              Delete Address
            </h3>
            <p className="text-sm mb-5 text-neutral-600 dark:text-neutral-400">
              Are you sure you want to remove this address?
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirmId(null)}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold border border-neutral-200 text-neutral-600 transition hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
